
import React, { useState, useContext, useEffect } from 'react';
import ChartBase, { useChartBase, humanizeNumber } from './chart-base';
import TweenedNumber from 'core/ui/tweened-number';
import SessionContext from 'SessionContext';
import { Input, Tooltip, Button } from 'reactstrap';
import LabelWithTooltip from './label-with-tooltip';

const GeoBoundaryWidget = (props) => {
  const base = useChartBase(props);
  const context = useContext(SessionContext);
  const [search, setSearch] = useState('');
  const hasLifeEvents = context.crossfilterStore.filtersContainLifeEvents();
  const [prefix, setPrefix] = useState("");
  // Use custom renderChart if provided, otherwise use default
  const chartRenderer = props.renderChart
    ? () => props.renderChart({ ...props, base })
    : () => renderChart(props);

  const classNames = `count-widget geoboundary-chart${props.config.excluded ? ' is-negated' : ''}`;

  useEffect(() =>{
    if(!base.excluded) return;
    if(!props.filters?.length) {
      base.onFilterNegate(false); 
    }
  }, [props.filters, base]);

  useEffect(() => {
    setPrefix(context.crossfilterStore.geoPrefix);
  }, [context.crossfilterStore.geoPrefix])

  const onSearchChange = (ev) => {
    setSearch(ev.target.value);
    base.onChartUpdate && base.onChartUpdate();
  };

  // Currently only for Zip Codes
  const onSearchPaste = (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    const chart = props.config;
    const value_series = chart.ds + '.' + chart.id;
    let filters = context.crossfilterStore.filters[chart.id] || [];
    const entries = (ev.clipboardData.getData('text/plain').match(/[0-9]{5}/g) || [])
      .filter((zip) => !filters.some((f) => f.value === zip));
    const new_filters = entries.map((entry) => ({
      series: value_series,
      value: entry,
      isNegated: false,
    }));
    filters = filters.concat(new_filters);
    context.crossfilterStore.set_filters(chart.id, filters);
    props.onChartUpdate && props.onChartUpdate();
  };

  // Geo-boundary sorting
  const getValues = () => {
    const config = props.config;
    let filters = props.filters || [];
    let value_series = config.ds + '.' + config.id;
    let all_values = props.values[value_series] || {};
    if(props.viewOnly){
      if(hasLifeEvents){
        value_series = 'xcounts__le_geo.le_' + config.id;
      } else {
        value_series = 'xcounts__geo.' + config.id;
      }
      all_values = context.crossfilterStore.counts_filtered[value_series] || {};
      if(!filters.length){
        filters = context.crossfilterStore.filters[`${prefix}${config.id}`] || [];
        if(filters.some((f) => f.is_negated)){
          props.config.excluded = "excluded: ";
        }
      }
    }
    const chart_values = filters.map((filter) => {
      const friendly_name = filter.friendly_name || filter.value;
      const name = filter.value;
      const count = all_values[filter.value] || 0;
      return [friendly_name, name, count];
    });
    // sort Congressional Districts and Counties by US State acronym
    let is_congress = `${prefix}congress`;
    let is_cbsa = `${prefix}cbsa`;
    let is_county_fips = `${prefix}county_fips`;
    if (
      config.id === is_congress ||
      config.id === is_cbsa ||
      config.id === is_county_fips
    ) {
      chart_values.sort((a, b) => {
        const bState = b[0].split('-')[1];
        const aState = a[0].split('-')[1];
        if (aState < bState) {
          return -1;
        }
        if (aState > bState) {
          return 1;
        }
        return -1;
      });
    } else {
      // sort zipcodes ascending order
      chart_values.sort((a, b) => a[1] - b[1]);
    }
    return chart_values;
  };

  const onKeyPress = (ev) => {
    if (ev.which === 17 || ev.which === 91 || !props) {
      return;
    }
    const geoType = props.config.id;
    let regXFilter = '';
    let maxLength = 99;
    switch (geoType) {
      case `${prefix}county_fips`:
        regXFilter = /[a-zA-Z ]+/;
        break;
      case `${prefix}zip`:
        regXFilter = /[0-9]/;
        maxLength = 4;
        break;
      case `${prefix}congress`:
        regXFilter = /[a-zA-Z0-9 ]/;
        break;
      case `${prefix}cbsa`:
        regXFilter = /[a-zA-Z0-9 ]/;
        break;
      default:
        break;
    }
    if (!regXFilter.test(ev.key) || ev.target.value.length > maxLength) {
      ev.preventDefault();
    }
  };

  const renderSummary = () => {
    const values = getValues();
    return <ul> {values.map(([value])=> <li key={value}>{value}</li> )} </ul>;
  }

  const onKeyUp = (ev) => {
    if (ev.which === 13 && search.length === 5) {
      const chart = props.config;
      const value_series = chart.ds + '.' + chart.id;
      context.crossfilterStore.add_filter(chart.id, value_series, search, false);
      setSearch('');
    }
  };

  const onRemoveEntry = (ev) => {
    ev.preventDefault();
    const chart = props.config;
    const value_series = chart.ds + '.' + chart.id;
    const currID = ev.currentTarget.id;
    const entries = props.filters.find(
      (x) => x.value === currID || x.friendly_name === currID
    );
    if(!props.filters.length) { base.onFilterNegate(false); }
    context.crossfilterStore.remove_filter(chart.id, value_series, entries.value);
    props.onChartUpdate && props.onChartUpdate();
  };

  const onUploadFile = (e) => {
    let zipList = e.split(',');
    const chart = props.config;
    const value_series = chart.ds + '.' + chart.id;
    context.crossfilterStore.add_filter(chart.id, value_series, zipList, false);
  };

  const shouldTween = props.tween;
  const values = getValues();
  const viewOnly = props.viewOnly;
  const isZip = props.config.id === `${prefix}zip`;
  const isCongress = props.config.id === `${prefix}congress`;
  const isCBSA = props.config.id === `${prefix}cbsa`;
  const isCounty_fips = props.config.id === `${prefix}county_fips`;
  const isNegated = props.config.excluded ? true : false;

  // Placeholder handlers for fly-to actions
  const handleFlyToZipcode = (value) => {
    if (base.handleFlyToZipcode) base.handleFlyToZipcode(value);
  };
  const handleFlyToCounty = (id) => {
    if (base.handleFlyToCounty) base.handleFlyToCounty(id);
  };
  const handleFlyToCongress = (id) => {
    if (base.handleFlyToCongress) base.handleFlyToCongress(id);
  };
  const handleFlyToCBSA = (id) => {
    if (base.handleFlyToCBSA) base.handleFlyToCBSA(id);
  };
  
  function renderChart(chartProps){
    const {base, ...props} = chartProps || {};
    return(
      <>
      {props.summarize &&
        <div className='summary'>
          {!props.isFiltered && <p>No Filters</p> }
          {props.isFiltered && renderSummary()}
        </div>
      }
      {!viewOnly && isZip && (
        <>
          <Input
            type="search"
            placeholder="Enter or Paste Values"
            onKeyUp={onKeyUp}
            onKeyPress={onKeyPress}
            value={search}
            onChange={onSearchChange}
            onPaste={onSearchPaste}
          />
          <UploadZipFile fileContentHandler={onUploadFile} />
        </>
      )}
      {values.length === 0 && <p>No Filters Selected</p>}
      {values.map(([value, id, count]) => (
        <div
          className='flex-row'

          key={id || value}
        >
          {!viewOnly && isZip && (
            <span
              className="zip-flyto"
              onClick={() => handleFlyToZipcode(value)}
            >
              <i className="fa-solid fa-crosshairs" />
              &nbsp;
            </span>
          )}
          {!viewOnly && isCounty_fips && (
            <span
              className="zip-flyto"
              onClick={() => handleFlyToCounty(id)}
            >
              <i className="fa-solid fa-crosshairs" />
              &nbsp;
            </span>
          )}
          {!viewOnly && isCongress && (
            <span
              className="zip-flyto"
              onClick={() => handleFlyToCongress(id)}
            >
              <i className="fa-solid fa-crosshairs" />
              &nbsp;
            </span>
          )}
          {!viewOnly && isCBSA && (
            <span
              className="zip-flyto"
              onClick={() => handleFlyToCBSA(id)}
            >
              <i className="fa-solid fa-crosshairs" />
              &nbsp;
            </span>
          )}
          <div className={`value ${isNegated ? 'selected not' : ''}`} id={id || value}>
            <LabelWithTooltip
              label={value}
              parentProps={props}
              cssClass="inner"
            />
            <i className="spacer"></i>
            {shouldTween && <TweenedNumber className="count" value={count} />}
            {!shouldTween && (
              <span className="count">{humanizeNumber(count)}</span>
            )}
            {!viewOnly && (
              <Button
                color="link"
                size="sm"
                className="remove"
                id={id || value}
                key={id || value}
                onClick={onRemoveEntry}
              >
                <i className="bi bi-trash"></i>
              </Button>
            )}
          </div>
        </div>
      ))}
      </>
    )
  }
  return (
    <ChartBase 
      {...props} 
      base={base} 
      chartClassName={classNames}
      renderSummary={renderSummary}
    >
      <div className={classNames}>
        {chartRenderer()}
      </div>
    </ChartBase>
  );
};

export default GeoBoundaryWidget;


/**
 * File uploader specially setup for the zip code file upload.
 * @param fileContentHandler : the function that will handle the text data in the file.
 * @returns : an input component for the zip codes file upload.
 */
export const UploadZipFile = (props) => {
  const { fileContentHandler } = props;
  const styles = {
    uploadWrap: {
      marginBottom: '0.6em',
    },
    uploadLabel: {
      border: '1px solid #ced4da',
      width: '88%',
      padding: '3px 10px',
      borderRadius: '0.375rem',
      cursor: 'pointer',
      color: '#4a65a5',
      fontSize: '0.9em'
    },
    uploadInfo: {
      position: 'relative',
      color: '#4a65a5',
      cursor: 'info',
      marginLeft: '3%',
      fontSize: '18px',
      top: '2px'
    },
    uploadError: {
      fontSize: '0.9em',
      color: '#f2706c'
    }
  }

  const [showError, setShowError] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const suppress = e => {
    e.stopPropagation();
    e.preventDefault();
  };

  // handle dropped file
  const onDrop = async e => {
    e.stopPropagation();
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    let text = await file.text();

    if(isValidExtension(file) && isEmpty(text)) {
      if(fileContentHandler != null) fileContentHandler(text);
    } else {
      setShowError(true);
    }
  };

  // handle selected file
  const onChangeHandler = async e => {
    setShowError(false);
    const file = e.target.files[0];
    let text = await file.text();

    if(isValidExtension(file) && isEmpty(text)) {
      if(fileContentHandler != null) fileContentHandler(text);
    } else {
      setShowError(true);
    }
  };

  // validate the file extention
  const isValidExtension = (file) => {
    return file.type === 'text/plain' || file.type === 'text/csv';
  }

  // validate the file is not empty and contains only numbers
  const isEmpty = (text) => {
    let numbersOnly = true;
    text.split(",").forEach(zip => {
      const zipTrim = zip.trim();
      if(!zipTrim.match(/^[0-9]*$/)) {
        numbersOnly = false;
      }
    });
    return text.length && numbersOnly;
  }

  return (
    <div onDrop={onDrop} onDragEnter={suppress} onDragOver={suppress} style={styles.uploadWrap}>
      <form method="post" action="#" id="#">
        <label style={styles.uploadLabel}>
          <span onMouseEnter={() => { if(showError) setShowError(false)}}><i className="bi bi-file-earmark-arrow-up-fill"></i> Upload .txt or .csv file</span>
          <input
            type="file"
            name="file"
            onChange={onChangeHandler}
            style={{ display: "none" }}
          />
        </label>
        <span style={styles.uploadInfo}>
          <i className="bi bi-info-square-fill" id="TooltipExample" />
        </span>
        <Tooltip
          isOpen={showHelp}
          delay={{ show: 100, hide: 500 }}
          placement="top"
          target="TooltipExample"
          toggle={() => { setShowHelp(!showHelp) }}
          style={{ backgroundColor: '#dddddd', color: '#333333', padding: '10px 15px', fontWeight: '500' }}
          >
            Upload a .txt or .csv file with a comma separated list of valid zip codes.
          </Tooltip>
      </form>
      {showError && <span style={styles.uploadError}>Error reading the file.</span>}
    </div>
  );
};
